// Mapping of Pine Script ta.* built-in functions to worker JS equivalents

export interface TaFnDef {
  /** Name of the JS function available in the worker scope */
  jsName: string
  /** True if it returns a tuple {macd, signal, histogram} or {supertrend, direction} */
  returnsTuple?: boolean
  /** The keys of the tuple object returned, in order matching Pine destructuring */
  tupleKeys?: string[]
}

/** Map from Pine's ta.XXX name to worker function info */
export const TA_BUILTINS: Record<string, TaFnDef> = {
  sma: { jsName: 'sma' },
  ema: { jsName: 'ema' },
  rma: { jsName: '_rma' },
  wma: { jsName: '_wma' },
  vwma: { jsName: '_vwma' },
  rsi: { jsName: 'rsi' },
  macd: { jsName: 'macd', returnsTuple: true, tupleKeys: ['macd', 'signal', 'histogram'] },
  atr: { jsName: 'atr' },
  stdev: { jsName: '_stdev' },
  highest: { jsName: 'highest' },
  lowest: { jsName: 'lowest' },
  change: { jsName: '_change' },
  crossover: { jsName: '_crossover' },
  crossunder: { jsName: '_crossunder' },
  cross: { jsName: '_cross' },
  sum: { jsName: '_tsum' },
  cum: { jsName: '_cum' },
  barssince: { jsName: '_barssince' },
  supertrend: { jsName: '_supertrend', returnsTuple: true, tupleKeys: ['supertrend', 'direction'] },
  stoch: { jsName: '_stoch' },
  cci: { jsName: '_cci' },
  obv: { jsName: '_obv' },
  linreg: { jsName: '_linreg' },
  dev: { jsName: '_dev' },
  variance: { jsName: '_variance' },
  correlation: { jsName: '_correlation' },
  bb: {
    jsName: '_bb',
    returnsTuple: true,
    tupleKeys: ['middle', 'upper', 'lower'],
  },
  kc: {
    jsName: '_kc',
    returnsTuple: true,
    tupleKeys: ['middle', 'upper', 'lower'],
  },
  dmi: {
    jsName: '_dmi',
    returnsTuple: true,
    tupleKeys: ['plus', 'minus', 'adx'],
  },
}

/** The JS helper code injected at the top of every transpiled indicator */
export const SERIES_HELPERS = `
// ── Series operation helpers ──────────────────────────────────────────────
function $sop(a, b, fn) {
  if (!Array.isArray(a) && !Array.isArray(b)) return fn(a, b);
  const len = Array.isArray(a) ? a.length : b.length;
  return Array.from({length:len}, (_,i) => {
    const va = Array.isArray(a) ? a[i] : a;
    const vb = Array.isArray(b) ? b[i] : b;
    return va!==null&&va!==undefined&&vb!==null&&vb!==undefined ? fn(va,vb) : null;
  });
}
function $shift(arr,n) {
  return arr.map((_,i) => i>=n ? arr[i-n] : null);
}
function $neg(a) {
  return Array.isArray(a) ? a.map(v=>v!==null?-v:null) : -a;
}
function $not(a) {
  return Array.isArray(a) ? a.map(v=>v!==null?!v:null) : !a;
}
function $ternary(c,a,b) {
  if(!Array.isArray(c)&&!Array.isArray(a)&&!Array.isArray(b)) return c?a:b;
  const len=[c,a,b].find(Array.isArray).length;
  return Array.from({length:len},(_,i)=>{
    const cv=Array.isArray(c)?c[i]:c;
    const av=Array.isArray(a)?a[i]:a;
    const bv=Array.isArray(b)?b[i]:b;
    return cv ? av : bv;
  });
}
function $fillNa(a,b) {
  if(!Array.isArray(a)) return a!==null&&a!==undefined?a:b;
  return a.map((v,i)=>v!==null&&v!==undefined?v:(Array.isArray(b)?b[i]:b));
}
// ── Extra TA functions ────────────────────────────────────────────────────
function _rma(src, period) {
  const out=Array(src.length).fill(null);
  let prev=null;
  for(let i=0;i<src.length;i++){
    const v=src[i];
    if(v===null)continue;
    if(prev===null){
      let sum=0,cnt=0;
      for(let j=Math.max(0,i-period+1);j<=i;j++){if(src[j]!==null){sum+=src[j];cnt++;}}
      if(cnt>=period){prev=sum/period;out[i]=prev;}
    } else {
      prev=(v+(period-1)*prev)/period;out[i]=prev;
    }
  }
  return out;
}
function _wma(src,period) {
  const out=Array(src.length).fill(null);
  const w=period*(period+1)/2;
  for(let i=period-1;i<src.length;i++){
    let sum=0,wsum=0;
    for(let j=0;j<period;j++){
      const v=src[i-j];
      if(v!==null){sum+=v*(period-j);wsum+=period-j;}
    }
    if(wsum>0)out[i]=sum/wsum;
  }
  return out;
}
function _vwma(src,vol,period) {
  const out=Array(src.length).fill(null);
  for(let i=period-1;i<src.length;i++){
    let s=0,v=0;
    for(let j=0;j<period;j++){
      const sv=src[i-j],vv=vol[i-j];
      if(sv!==null&&vv!==null){s+=sv*vv;v+=vv;}
    }
    if(v>0)out[i]=s/v;
  }
  return out;
}
function _stdev(src,period) {
  const m=sma(src,period);
  const out=Array(src.length).fill(null);
  for(let i=period-1;i<src.length;i++){
    if(m[i]===null)continue;
    let ss=0;let cnt=0;
    for(let j=0;j<period;j++){const v=src[i-j];if(v!==null){ss+=(v-m[i])*(v-m[i]);cnt++;}}
    out[i]=cnt>0?Math.sqrt(ss/cnt):null;
  }
  return out;
}
function _change(src,length=1) {
  return src.map((_,i)=>i>=length&&src[i]!==null&&src[i-length]!==null?src[i]-src[i-length]:null);
}
function _crossover(a,b) {
  const pa=$shift(a,1),pb=$shift(b,1);
  return a.map((_,i)=>pa[i]!==null&&pb[i]!==null&&b[i]!==null?pa[i]<=pb[i]&&a[i]>b[i]:false);
}
function _crossunder(a,b) {
  const pa=$shift(a,1),pb=$shift(b,1);
  return a.map((_,i)=>pa[i]!==null&&pb[i]!==null&&b[i]!==null?pa[i]>=pb[i]&&a[i]<b[i]:false);
}
function _cross(a,b){return a.map((_,i)=>_crossover(a,b)[i]||_crossunder(a,b)[i]);}
function _tsum(src,length) {
  const out=Array(src.length).fill(null);
  for(let i=length-1;i<src.length;i++){
    let s=0;let ok=true;
    for(let j=0;j<length;j++){if(src[i-j]===null){ok=false;break;}s+=src[i-j];}
    if(ok)out[i]=s;
  }
  return out;
}
function _cum(src) {
  const out=Array(src.length).fill(null);
  let s=0;
  for(let i=0;i<src.length;i++){if(src[i]!==null){s+=src[i];out[i]=s;}}
  return out;
}
function _barssince(cond) {
  const out=Array(cond.length).fill(null);
  let cnt=null;
  for(let i=0;i<cond.length;i++){
    if(cond[i]){cnt=0;}else if(cnt!==null){cnt++;}
    out[i]=cnt;
  }
  return out;
}
function _supertrend(factor,period) {
  const hl2=close.map((_,i)=>high[i]!==null&&low[i]!==null?(high[i]+low[i])/2:null);
  const atrArr=atr(high,low,close,period);
  const n=close.length;
  const upper=Array(n).fill(null),lower=Array(n).fill(null);
  const superArr=Array(n).fill(null),dirArr=Array(n).fill(null);
  for(let i=1;i<n;i++){
    if(hl2[i]===null||atrArr[i]===null)continue;
    const ub=hl2[i]+factor*atrArr[i];
    const lb=hl2[i]-factor*atrArr[i];
    upper[i]=upper[i-1]!==null&&ub>upper[i-1]?upper[i-1]:ub;
    lower[i]=lower[i-1]!==null&&lb<lower[i-1]?lower[i-1]:lb;
    if(superArr[i-1]===upper[i-1]){
      superArr[i]=close[i]>upper[i]?lower[i]:upper[i];
    } else {
      superArr[i]=close[i]<lower[i]?upper[i]:lower[i];
    }
    dirArr[i]=superArr[i]===upper[i]?-1:1;
  }
  return {supertrend:superArr,direction:dirArr};
}
function _stoch(src,high_,low_,period) {
  const out=Array(src.length).fill(null);
  for(let i=period-1;i<src.length;i++){
    let lo=Infinity,hi=-Infinity;
    for(let j=0;j<period;j++){
      if(high_[i-j]!==null&&high_[i-j]>hi)hi=high_[i-j];
      if(low_[i-j]!==null&&low_[i-j]<lo)lo=low_[i-j];
    }
    out[i]=(hi-lo)>0?100*(src[i]-lo)/(hi-lo):0;
  }
  return out;
}
function _cci(src,period) {
  const m=sma(src,period);
  const out=Array(src.length).fill(null);
  for(let i=period-1;i<src.length;i++){
    if(m[i]===null)continue;
    let md=0;
    for(let j=0;j<period;j++){if(src[i-j]!==null)md+=Math.abs(src[i-j]-m[i]);}
    md/=period;
    out[i]=md>0?(src[i]-m[i])/(0.015*md):0;
  }
  return out;
}
function _obv(src,vol) {
  const out=Array(src.length).fill(null);
  let v=0;
  for(let i=1;i<src.length;i++){
    if(src[i]===null||src[i-1]===null||vol[i]===null)continue;
    if(src[i]>src[i-1])v+=vol[i];
    else if(src[i]<src[i-1])v-=vol[i];
    out[i]=v;
  }
  return out;
}
function _linreg(src,period,offset=0) {
  const out=Array(src.length).fill(null);
  for(let i=period-1+offset;i<src.length;i++){
    let sx=0,sy=0,sxy=0,sxx=0;
    for(let j=0;j<period;j++){
      const v=src[i-offset-j];
      if(v===null){sx=NaN;break;}
      sx+=j;sy+=v;sxy+=j*v;sxx+=j*j;
    }
    if(isNaN(sx))continue;
    const n=period,d=n*sxx-sx*sx;
    if(d===0)continue;
    const m=(n*sxy-sx*sy)/d,b=(sy-m*sx)/n;
    out[i]=m*offset+b;
  }
  return out;
}
function _dev(src,period){return _stdev(src,period);}
function _variance(src,period){const sd=_stdev(src,period);return sd.map(v=>v!==null?v*v:null);}
function _correlation(src1,src2,period){
  const out=Array(src1.length).fill(null);
  for(let i=period-1;i<src1.length;i++){
    let s1=0,s2=0,s12=0,s11=0,s22=0;
    for(let j=0;j<period;j++){
      const a=src1[i-j],b=src2[i-j];
      if(a===null||b===null){s1=NaN;break;}
      s1+=a;s2+=b;s12+=a*b;s11+=a*a;s22+=b*b;
    }
    if(isNaN(s1))continue;
    const n=period,d=Math.sqrt((n*s11-s1*s1)*(n*s22-s2*s2));
    out[i]=d>0?(n*s12-s1*s2)/d:0;
  }
  return out;
}
function _bb(src,period,mult) {
  const m=sma(src,period),sd=_stdev(src,period);
  const upper=m.map((v,i)=>v!==null&&sd[i]!==null?v+mult*sd[i]:null);
  const lower=m.map((v,i)=>v!==null&&sd[i]!==null?v-mult*sd[i]:null);
  return {middle:m,upper,lower};
}
function _kc(src,high_,low_,period,mult) {
  const m=ema(src,period),r=_rma(atr(high_,low_,src,period),period);
  const upper=m.map((v,i)=>v!==null&&r[i]!==null?v+mult*r[i]:null);
  const lower=m.map((v,i)=>v!==null&&r[i]!==null?v-mult*r[i]:null);
  return {middle:m,upper,lower};
}
function _dmi(period,smooth) {
  const n=close.length;
  const trArr=Array(n).fill(null),pDM=Array(n).fill(null),nDM=Array(n).fill(null);
  for(let i=1;i<n;i++){
    const hl=high[i]-low[i],hc=Math.abs(high[i]-close[i-1]),lc=Math.abs(low[i]-close[i-1]);
    trArr[i]=Math.max(hl,hc,lc);
    const upMove=high[i]-high[i-1],downMove=low[i-1]-low[i];
    pDM[i]=upMove>downMove&&upMove>0?upMove:0;
    nDM[i]=downMove>upMove&&downMove>0?downMove:0;
  }
  const trS=_rma(trArr,period),pDMS=_rma(pDM,period),nDMS=_rma(nDM,period);
  const pDI=trS.map((v,i)=>v!==null&&pDMS[i]!==null?100*pDMS[i]/v:null);
  const nDI=trS.map((v,i)=>v!==null&&nDMS[i]!==null?100*nDMS[i]/v:null);
  const dx=pDI.map((v,i)=>v!==null&&nDI[i]!==null&&(v+nDI[i])!==0?100*Math.abs(v-nDI[i])/(v+nDI[i]):null);
  const adx=_rma(dx,smooth);
  return {plus:pDI,minus:nDI,adx};
}
`.trim()
